// src/components/ParseDocumentProgressDialog.js

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
  makeStyles,
  useTheme,
  useMediaQuery,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Fade,
} from "@material-ui/core";
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  TextFields as TextFieldsIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Check as CheckIcon,
} from "@material-ui/icons";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      minWidth: "unset", // Allow it to be smaller on mobile
    },
    [theme.breakpoints.up("sm")]: {
      minWidth: 400,
      maxWidth: 600,
    },
  },
  dialogActions: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      paddingBottom: theme.spacing(3), // Extra padding at the bottom on mobile
    },
  },
  progressSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  progressText: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  fileInfo: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  fileIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  fileName: {
    fontWeight: 500,
    flexGrow: 1,
  },
  fileSize: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  stepIcon: {
    color: theme.palette.primary.main,
  },
  activeStepIcon: {
    color: theme.palette.primary.main,
    // animation: "$pulse 1.5s infinite", // Temporarily remove animation
  },
  completedStepIcon: {
    color: theme.palette.success.main,
  },
  stepContent: {
    paddingBottom: theme.spacing(2),
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.6,
    },
    "100%": {
      opacity: 1,
    },
  },
  timeEstimate: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontStyle: "italic",
  },
  successIcon: {
    color: theme.palette.success.main,
    fontSize: 48,
    margin: theme.spacing(2, 0),
  },
  successContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  successMessage: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  stepLabel: {
    fontWeight: 500,
  },
  stepDescription: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  progressBarContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

// Steps in the document parsing process
const steps = [
  {
    label: "Uploading Files",
    description: "Transferring your documents to our secure server...",
    icon: CloudUploadIcon,
    estimatedTime: 2, // seconds
  },
  {
    label: "Extracting Text",
    description: "Reading and extracting text from your documents...",
    icon: TextFieldsIcon,
    estimatedTime: 10, // seconds
  },
  {
    label: "Analyzing Content",
    description: "Using AI to understand your professional information...",
    icon: DescriptionIcon,
    estimatedTime: 15, // seconds
  },
  {
    label: "Processing Personal Info",
    description: "Identifying your contact details and personal information...",
    icon: PersonIcon,
    estimatedTime: 5, // seconds
  },
  {
    label: "Processing Education",
    description: "Extracting your educational background and achievements...",
    icon: SchoolIcon,
    estimatedTime: 5, // seconds
  },
  {
    label: "Processing Experience",
    description: "Analyzing your work history and professional experience...",
    icon: WorkIcon,
    estimatedTime: 8, // seconds
  },
  {
    label: "Finalizing",
    description: "Preparing your information for the application builder...",
    icon: CheckIcon,
    estimatedTime: 3, // seconds
  },
];

// Calculate total estimated time
const totalEstimatedTime = steps.reduce(
  (total, step) => total + step.estimatedTime,
  0
);

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Format time remaining
const formatTimeRemaining = (seconds) => {
  if (seconds < 60) {
    return `${Math.ceil(seconds)} seconds`;
  } else {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.ceil(seconds % 60);
    return `${minutes} min ${remainingSeconds} sec`;
  }
};

// Custom step icon component
const CustomStepIcon = ({ active, completed, icon }) => {
  const classes = useStyles();
  const StepIcon = steps[icon - 1].icon;

  return (
    <div
      className={clsx({
        [classes.stepIcon]: !active && !completed,
        [classes.activeStepIcon]: active,
        [classes.completedStepIcon]: completed,
      })}
    >
      {completed ? (
        <CheckIcon />
      ) : active ? (
        <StepIcon /> // Temporarily remove CircularProgress
      ) : (
        <StepIcon />
      )}
    </div>
  );
};

const ParseDocumentProgressDialog = ({
  open,
  onClose,
  files,
  progress,
  success,
  error,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [activeStep, setActiveStep] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(totalEstimatedTime);
  const [visualProgress, setVisualProgress] = useState(0); // Used for animation

  console.log("ParseDocumentProgressDialog: Props received:", {
    open,
    files,
    progress, // Actual backend progress
    success,
    error,
  });

  // Calculate total file size
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);

  // Effect for self-driven progress animation
  useEffect(() => {
    let animationTimer;
    if (open && !success && !error) {
      console.log(
        "ParseDocumentProgressDialog: Starting self-driven animation."
      );
      setVisualProgress(0); // Reset visual progress on open
      setActiveStep(0);
      setTimeRemaining(totalEstimatedTime);

      const totalDuration = totalEstimatedTime * 1000; // ms
      const intervalTime = 100; // Update every 100ms
      const progressIncrement = (intervalTime / totalDuration) * 95; // Aim for 95% via animation

      animationTimer = setInterval(() => {
        setVisualProgress((prev) => {
          if (prev >= 95) {
            clearInterval(animationTimer);
            return 95;
          }
          return Math.min(prev + progressIncrement, 95);
        });
        setTimeRemaining((prev) => Math.max(prev - intervalTime / 1000, 0));
      }, intervalTime);
    }

    return () => {
      if (animationTimer) {
        console.log(
          "ParseDocumentProgressDialog: Clearing self-driven animation timer."
        );
        clearInterval(animationTimer);
      }
    };
  }, [open, success, error]); // Rerun if dialog opens/closes or process ends

  // Effect to update stepper and final progress based on visualProgress or actual success/error
  useEffect(() => {
    if (!open) return;

    if (success) {
      console.log(
        "ParseDocumentProgressDialog: Success is true. Snapping to 100%."
      );
      setVisualProgress(100);
      setActiveStep(steps.length);
      setTimeRemaining(0);
    } else if (error) {
      // Keep visual progress where it was, or reset if desired
      console.log("ParseDocumentProgressDialog: Error occurred.");
      // Optionally, stop visual progress: setVisualProgress(prev => prev);
    } else {
      // Update active step based on visualProgress
      const stepCount = steps.length;
      const newActiveStep = Math.min(
        Math.floor((visualProgress / 100) * stepCount),
        stepCount - 1
      );
      setActiveStep(newActiveStep);
      // console.log("ParseDocumentProgressDialog: Visual progress:", visualProgress, "New active step:", newActiveStep);
    }
  }, [visualProgress, success, error, open]);

  // Fallback: If actual progress prop jumps to 100, ensure UI reflects completion
  // This handles cases where the animation might lag or if `success` prop is delayed
  useEffect(() => {
    if (progress === 100 && !success && open && !error) {
      console.log(
        "ParseDocumentProgressDialog: Actual progress hit 100, ensuring UI completion."
      );
      setVisualProgress(100);
      setActiveStep(steps.length);
      setTimeRemaining(0);
      // It might be good to also set the `success` prop true here if the parent can handle it,
      // or rely on the parent to eventually set `success` true.
    }
  }, [progress, success, open, error]);

  return (
    <Dialog
      open={open}
      onClose={success || error ? onClose : undefined} // Allow close only on success/error
      aria-labelledby="parse-progress-dialog-title"
      fullWidth={isMobile}
      maxWidth={isMobile ? "xs" : "md"}
    >
      <DialogTitle id="parse-progress-dialog-title">
        {success
          ? "Document Processing Complete"
          : error
          ? "Error Processing Documents"
          : "Processing Your Documents"}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* File information */}
        {files.length > 0 && !success && !error && (
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              Processing {files.length} file{files.length !== 1 ? "s" : ""}:
            </Typography>

            {files.map((file, index) => (
              <Paper key={index} className={classes.fileInfo} elevation={0}>
                <DescriptionIcon className={classes.fileIcon} />
                <Typography className={classes.fileName} noWrap>
                  {file.name}
                </Typography>
                <Typography className={classes.fileSize}>
                  {formatFileSize(file.size)}
                </Typography>
              </Paper>
            ))}

            <Typography variant="body2" color="textSecondary">
              Total size: {formatFileSize(totalSize)}
            </Typography>
          </Box>
        )}

        {/* Success message */}
        {success && (
          <Fade in={success}>
            <Box className={classes.successContainer}>
              <CheckIcon className={classes.successIcon} />
              <Typography variant="h6" className={classes.successMessage}>
                Your documents have been successfully processed!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                The application form has been filled with information extracted
                from your documents.
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Error message */}
        {error && (
          <Box textAlign="center" my={3}>
            <Typography color="error" variant="h6">
              {error}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              Please try again or upload a different document.
            </Typography>
          </Box>
        )}

        {/* Progress stepper */}
        {!success && !error && (
          <>
            <Box className={classes.progressBarContainer}>
              <LinearProgress
                key={`linear-visual-${visualProgress}`} // Keyed by visualProgress
                variant="determinate"
                value={visualProgress} // Driven by visualProgress
                className={classes.progressBar}
              />
              <Box className={classes.progressText}>
                <Typography variant="body2">
                  {Math.round(visualProgress)}% complete
                </Typography>
                <Typography variant="body2">
                  {timeRemaining > 0 && !success // Hide time if success
                    ? `Estimated time remaining: ${formatTimeRemaining(
                        timeRemaining
                      )}`
                    : success
                    ? "Completed!"
                    : "Almost done..."}
                </Typography>
              </Box>
            </Box>

            <Stepper
              key={`stepper-visual-${activeStep}`} // Keyed by activeStep (derived from visualProgress)
              activeStep={activeStep}
              orientation="vertical"
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={(props) => <CustomStepIcon {...props} />}
                  >
                    <Typography className={classes.stepLabel}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent className={classes.stepContent}>
                    <Typography className={classes.stepDescription}>
                      {step.description}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {(success || error) && (
          <Button onClick={onClose} color="primary" variant="contained">
            {success ? "Continue" : "Close"}
          </Button>
        )}
        {!success && !error && (
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ParseDocumentProgressDialog;
